import { FC, useEffect, useRef } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { parseHTML } from '@/lib/parse';

const Switcher: FC<any> = ({ data, activeSlide, changeSlide }) => {
	const switcherWrapper = useRef<any>(null);

	useEffect(() => {
		switcherWrapper.current?.classList?.add('loaded');
	}, [switcherWrapper]);

	return (
		<SwitcherWrapper ref={switcherWrapper}>
			<ActiveTabBackground $activeSlide={activeSlide} />

			{data?.map((slide, index) => (
				<Tab
					key={`tab_${index}`}
					$currentSlide={index}
					$activeSlide={activeSlide}
					onClick={() => activeSlide !== index && changeSlide(index)}
				>
					<TabRow>
						<TabTitle>{parseHTML(slide.tabTitle)}</TabTitle>

						{index === activeSlide && (
							<SVGLoader viewBox="0 0 30 30" className="header-slider__loader">
								<CircleLoader
									r="12"
									cx="15"
									cy="15"
									className="hider-slider__circle"
								/>
							</SVGLoader>
						)}
					</TabRow>
				</Tab>
			))}
		</SwitcherWrapper>
	);
};

const circle = keyframes`
  0% {
    stroke-dashoffset: 74;
    opacity: 0;
  }
  4% {
    stroke-dashoffset: 74;
    opacity: 1;
  }
  90% {
    stroke-dashoffset: 0;
    opacity: 1;
  }
  93% {
    stroke-dashoffset: 0;
    opacity: 0;
  }
  100% {
    stroke-dashoffset: 0;
    opacity: 0;
  }
`;

const SwitcherWrapper = styled.div`
	position: absolute;
	top: calc(50% + 33px);
	left: 0;
	transform: translateY(-50%);
	z-index: 1300;

	&.loaded {
		circle {
			animation: ${circle} 8s linear 1 forwards;
		}
	}

	@media (max-width: 900px) {
		top: 140px;
		transform: translateY(0);
		display: flex;
		align-items: center;
	}

	@media (max-width: 600px) {
		bottom: 30px;
		left: 50%;
		transform: translateX(-50%);
		top: auto;
	}
`;

const ActiveTabBackground = styled.div<{ $activeSlide: number }>`
	position: absolute;
	background-color: #f5f5f5;
	height: 70px;
	width: 100%;
	transform: translateY(${({ $activeSlide }) => $activeSlide * 100}%);
	z-index: 19;
	transition: 0.2s linear;
	--smooth-corners: 11.34, 3.6;
	mask-image: paint(smooth-corners);

	@media (max-width: 900px) {
		display: none;
	}
`;

const TabRow = styled.div`
	display: flex;
	align-items: center;
	margin-top: auto;
	margin-bottom: auto;
`;

const Tab = styled.div<{ $activeSlide: number; $currentSlide: number }>`
	display: flex;
	flex-direction: column;
	border-radius: 18px;
	padding-left: 20px;
	padding-right: 50px;
	width: 215px;
	height: 70px;
	position: relative;
	z-index: 20;
	cursor: pointer;

	@media (max-width: 1050px) {
		width: 185px;
		padding-left: 15px;
		padding-right: 20px;
	}

	@media (max-width: 900px) {
		width: 20px;
		height: 20px;
		display: flex;
		padding: 0;
		margin: 0;
		margin-right: 23px;

		&:after {
			content: '';
			position: absolute;
			top: 50%;
			left: 50%;
			width: 4px;
			height: 4px;
			background-color: var(--color-purple);
			border-radius: 50%;
			transform: translateX(-50%) translateY(-50%);
			transition:
				width 0.5s,
				height 0.5s;

			${({ $activeSlide, $currentSlide }) =>
				$activeSlide === $currentSlide &&
				css`
					width: 13px;
					height: 13px;
				`}
		}
	}

	@media (max-width: 600px) {
		margin-right: 10px;
		margin-left: 10px;
	}

	${({ $activeSlide, $currentSlide }) =>
		$activeSlide === $currentSlide &&
		css`
			h4 {
				color: var(--color-purple) !important;
			}
		`}

	&:hover {
		h4 {
			color: var(--color-purple) !important;
		}
	}
`;

const SVGLoader = styled.svg`
	position: absolute;
	right: 17px;
	width: 30px;
	min-width: 30px;
	height: 30px;
	transform: rotate(-90deg);
	stroke-linecap: round;
	stroke-width: 2;
	fill: none;

	@media (max-width: 1050px) {
		transform: rotate(-90deg) scale(0.85);
	}

	@media (max-width: 900px) {
		position: absolute;
		width: 100%;
		top: -5px;
		left: -5px;
	}
`;

const CircleLoader = styled.circle`
	stroke: var(--color-purple);
	transform-origin: center;
	stroke-dasharray: 74;
	stroke-dashoffset: 74;
	opacity: 0;
`;

const TabTitle = styled.h4`
	font-family: var(--font-sans);

	font-weight: var(--font-medium);
	font-size: 14px;
	line-height: 20px;
	color: var(--color-purple-50);
	mix-blend-mode: normal;
	transition: 0.3s color;
	transition-delay: 0.1s;

	@media (max-width: 1050px) {
		font-size: 13px;
	}

	@media (max-width: 900px) {
		display: none;
	}
`;

export default Switcher;
